<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import type {
  AgentModule,
  SessionInfo,
  ThemeInfo,
} from "@mothership/agent-contracts";
import {
  createAgentContext,
  createEventBus,
  type HostEventBusController,
} from "@mothership/sdk-core";

interface RegistryItem {
  agentId: string;
  releaseId: string;
  name: string;
  description: string;
  frontendProfile: "vue" | "react";
  entryUrl: string;
  contractVersion: string;
  classification: string;
  manifestHash: string;
}

interface MountedAgent {
  item: RegistryItem;
  module: AgentModule;
  container: HTMLElement;
  bus: HostEventBusController;
  dirty: boolean;
}

const agents = ref<RegistryItem[]>([]);
const hostSession = ref<SessionInfo | null>(null);
const selectedAgentId = ref("");
const mountedAgent = ref<MountedAgent | null>(null);
const agentContainer = ref<HTMLElement | null>(null);
const loading = ref(true);
const error = ref("");
const mode = ref<"light" | "dark">(
  localStorage.getItem("mothership-theme") === "dark" ? "dark" : "light",
);
const route = ref(location.pathname);
const adminSection = ref("agents");
const adminRows = ref<Record<string, unknown>[]>([]);
const adminOverview = ref<Record<string, unknown> | null>(null);
const adminLoading = ref(false);
const adminActionBody = ref("{}");
const adminActionRunning = ref(false);
const adminActionResult = ref("");
const deletingAgentId = ref("");
const adminToken = ref("");
const adminTokenExpiresAt = ref(0);

const isAdmin = computed(() => route.value.startsWith("/admin"));
const currentAgent = computed(() =>
  agents.value.find((item) => item.agentId === selectedAgentId.value),
);
// The host's design tokens, mirrored from style.css. Agents receive these
// via ThemeInfo.tokens and are expected to style themselves with them.
const designTokens: Record<"light" | "dark", Record<string, string>> = {
  light: {
    surface: "#ffffff",
    "surface-2": "#f2f2f2",
    ink: "#252525",
    muted: "#5f5f5f",
    line: "#dedede",
    accent: "#d04a02",
    "accent-strong": "#b33a00",
    "accent-ink": "#ffffff",
    "accent-soft": "#fdeee5",
    danger: "#a32020",
    "font-family":
      'Inter, "Pretendard Variable", "Noto Sans KR", system-ui, sans-serif',
    radius: "10px",
  },
  dark: {
    surface: "#1a1a1a",
    "surface-2": "#242424",
    ink: "#f2f2f2",
    muted: "#a6a6a6",
    line: "#3a3a3a",
    accent: "#eb8c00",
    "accent-strong": "#eb8c00",
    "accent-ink": "#241000",
    "accent-soft": "#3a1e0d",
    danger: "#ff8080",
    "font-family":
      'Inter, "Pretendard Variable", "Noto Sans KR", system-ui, sans-serif',
    radius: "10px",
  },
};

const theme = computed<ThemeInfo>(() => ({
  mode: mode.value,
  density: "comfortable",
  tokens: designTokens[mode.value],
}));

const adminSections = [
  "agents",
  "claims",
  "capabilities",
  "bindings",
  "capability-grants",
  "resources",
  "spaces",
  "knowledge",
  "models",
  "tools",
  "runtime-profiles",
  "runs",
  "jobs",
  "operation-invocations",
  "triggers",
  "trigger-runs",
  "dead-letters",
  "sources",
  "principals",
  "directory-identities",
  "service-identities",
  "secrets",
  "usage",
  "billing",
  "plans",
  "limits",
  "price-books",
  "usage-evidence",
  "usage-rollups",
  "audit",
];

interface AdminAction {
  label: string;
  method: "POST" | "PUT";
  path: string;
  sample: Record<string, unknown>;
  form?: "agent-origin";
}

const adminActions: Partial<Record<string, AdminAction | AdminAction[]>> = {
  agents: [
    {
      label: "Signed manifests reload",
      method: "POST",
      path: "/api/admin/v1/agents/reload",
      sample: {},
    },
    {
      label: "Register agent",
      method: "POST",
      path: "/api/admin/v1/agents/register-url",
      sample: {},
      form: "agent-origin",
    },
  ],
  claims: {
    label: "Claim binding create",
    method: "POST",
    path: "/api/admin/v1/claims/bind",
    sample: {
      agentId: "document-review",
      claimId: "documents.process",
      principalType: "user",
      principalId: "",
      effect: "allow",
      reason: "",
    },
  },
  spaces: {
    label: "Shared space create",
    method: "POST",
    path: "/api/admin/v1/spaces",
    sample: {
      name: "Review workspace",
      scope: "space_shared",
      dataClassification: "confidential",
      retentionPolicy: {},
    },
  },
  sources: {
    label: "Connector upsert",
    method: "POST",
    path: "/api/admin/v1/connectors",
    sample: {
      connectorId: "approved-source",
      type: "http-json",
      baseUrl: "https://example.com/api",
      authType: "bearer",
      allowedMethods: ["GET"],
      egressPolicy: {
        allowed_hosts: ["example.com"],
        max_response_bytes: 2000000,
      },
    },
  },
  models: {
    label: "Model profile upsert",
    method: "POST",
    path: "/api/admin/v1/model-profiles",
    sample: {
      profileId: "answer-fast",
      provider: "openai-compatible",
      model: "auto",
      maxInputClassification: "confidential",
      maxOutputTokens: 4096,
    },
  },
  limits: {
    label: "Limit policy create",
    method: "POST",
    path: "/api/admin/v1/limits",
    sample: {
      type: "quota",
      meter: "llm.output_token",
      quantity: 1000000,
      period: "month",
      hard: true,
      agentId: "document-review",
      reason: "",
    },
  },
  "price-books": {
    label: "Immutable price book create",
    method: "POST",
    path: "/api/admin/v1/price-books",
    sample: {
      priceBookId: "standard-2026-07",
      currency: "USD",
      effectiveFrom: "2026-07-24T00:00:00Z",
      prices: {
        "llm.input_token": 1,
        "llm.output_token": 3,
      },
    },
  },
  principals: {
    label: "Automation principal create/update",
    method: "POST",
    path: "/api/admin/v1/automation-principals",
    sample: {
      agentId: "document-review",
      type: "agent_service",
      claims: ["documents.read", "documents.process"],
      owner: "",
      active: true,
      reason: "",
    },
  },
  triggers: {
    label: "Trigger instance create",
    method: "POST",
    path: "/api/admin/v1/triggers",
    sample: {
      agentId: "document-review",
      templateId: "daily-review-digest",
      automationPrincipalId: "",
      sourceConfig: {},
      concurrencyPolicy: "forbid",
      timeoutMs: 90000,
      reason: "",
    },
  },
  roles: {
    label: "Scoped role binding create",
    method: "POST",
    path: "/api/admin/v1/role-bindings",
    sample: {
      roleId: "",
      principalType: "user",
      principalId: "",
      scopeType: "organization",
      effect: "allow",
      reason: "",
    },
  },
};

const adminActionIndex = ref(0);
const sectionActions = computed<AdminAction[]>(() => {
  const entry = adminActions[adminSection.value];
  if (!entry) return [];
  return Array.isArray(entry) ? entry : [entry];
});
const adminAction = computed<AdminAction | undefined>(
  () => sectionActions.value[adminActionIndex.value],
);

function selectAdminAction(index: number) {
  adminActionIndex.value = index;
  resetAdminAction();
}

const agentOriginScheme = ref<"https" | "http">("https");
const agentOriginHost = ref("");

// Pasting a full URL just works: the scheme moves to the dropdown and the
// path/query/trailing slashes are stripped, leaving only host[:port].
// While typing (`force` false) we only react once a complete scheme is
// present, so partial input is never mangled; blur and submit normalize
// unconditionally.
function normalizeAgentOrigin(force = false) {
  let value = agentOriginHost.value.trim();
  const scheme = value.match(/^(https?):\/\//i);
  if (scheme?.[1]) {
    agentOriginScheme.value = scheme[1].toLowerCase() as "https" | "http";
    value = value.slice(scheme[0].length);
  } else if (!force) {
    return;
  }
  value = value
    .replace(/^\/+/, "")
    .replace(/^[^/?#@]*@/, "")
    .replace(/[/?#].*$/, "")
    .toLowerCase();
  agentOriginHost.value = value;
}

async function api<T>(
  path: string,
  init?: RequestInit,
  bearerToken = "",
): Promise<T> {
  const csrfToken =
    document.cookie
      .split("; ")
      .find((item) => item.startsWith("csrftoken="))
      ?.split("=")
      .slice(1)
      .join("=") ?? "";
  const method = (init?.method ?? "GET").toUpperCase();
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(bearerToken
        ? { Authorization: `Bearer ${bearerToken}` }
        : {}),
      ...(csrfToken && !["GET", "HEAD", "OPTIONS"].includes(method)
        ? { "X-CSRFToken": decodeURIComponent(csrfToken) }
        : {}),
      ...init?.headers,
    },
  });
  if (
    response.status === 401 &&
    !bearerToken &&
    !path.startsWith("/api/v1/auth/")
  ) {
    const next = encodeURIComponent(
      `${location.pathname}${location.search}${location.hash}`,
    );
    location.assign(`/api/v1/auth/login?next=${next}`);
    return new Promise<T>(() => undefined);
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(
      (payload as { message?: string }).message ??
        `Request failed (${response.status})`,
    );
  }
  return (await response.json()) as T;
}

async function getAdminToken(): Promise<string> {
  if (
    adminToken.value &&
    adminTokenExpiresAt.value > Date.now() + 30_000
  ) {
    return adminToken.value;
  }
  const exchanged = await api<{ token: string; expiresAt: string }>(
    "/api/v1/session/admin-token",
    {
      method: "POST",
      body: "{}",
    },
  );
  adminToken.value = exchanged.token;
  adminTokenExpiresAt.value = Date.parse(exchanged.expiresAt);
  return exchanged.token;
}

async function adminApi<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  return api<T>(path, init, await getAdminToken());
}

function parseRoute() {
  route.value = location.pathname;
  const match = location.pathname.match(/^\/agent\/([^/]+)/);
  if (match?.[1]) selectedAgentId.value = decodeURIComponent(match[1]);
}

function navigate(path: string) {
  if (
    mountedAgent.value?.dirty &&
    !window.confirm("저장하지 않은 변경사항이 있습니다. 이동하시겠습니까?")
  ) {
    return;
  }
  history.pushState({}, "", path);
  parseRoute();
}

// Mounting is a long await chain — module import, token exchange, the
// agent's own mount — and switching agents mid-chain used to start a second
// run against the same container while the first was still suspended. Both
// then mounted, but only the last one was tracked, leaving the earlier agent
// in the DOM with a live bus and no way to unmount it. Runs are serialised
// so they cannot interleave, and each carries a generation so a run that has
// been superseded while queued or suspended stops instead of mounting.
let mountGeneration = 0;
let mountQueue: Promise<unknown> = Promise.resolve();

function serializeMount(task: () => Promise<void>): Promise<void> {
  const run = mountQueue.then(task, task);
  mountQueue = run.catch(() => undefined);
  return run;
}

async function unmountCurrent(
  reason: "navigation" | "logout" | "shutdown" = "navigation",
) {
  mountGeneration += 1;
  return serializeMount(() => detachCurrent(reason));
}

async function detachCurrent(
  reason: "navigation" | "logout" | "shutdown" = "navigation",
) {
  const current = mountedAgent.value;
  if (!current) return;
  current.bus.emitToAgent("host:before-unmount", { reason });
  try {
    await current.module.unmount(current.container);
  } finally {
    current.bus.destroy();
    current.container.replaceChildren();
    mountedAgent.value = null;
  }
}

async function mountSelected() {
  const generation = ++mountGeneration;
  return serializeMount(() => attachSelected(generation));
}

async function attachSelected(generation: number) {
  const item = currentAgent.value;
  if (!item || !agentContainer.value || isAdmin.value) return;
  // A newer switch was requested while this run sat in the queue.
  if (generation !== mountGeneration) return;
  if (mountedAgent.value?.item.agentId === item.agentId) {
    const path = location.pathname
      .replace(`/agent/${encodeURIComponent(item.agentId)}`, "")
      .replace(/^\//, "");
    mountedAgent.value.bus.emitToAgent("host:navigate", { path });
    return;
  }
  loading.value = true;
  error.value = "";
  await detachCurrent();
  await nextTick();
  const container = agentContainer.value;
  if (!container) {
    loading.value = false;
    return;
  }
  try {
    if (generation !== mountGeneration) return;
    const imported = (await import(
      /* @vite-ignore */ item.entryUrl
    )) as Partial<AgentModule>;
    if (
      typeof imported.mount !== "function" ||
      typeof imported.unmount !== "function"
    ) {
      throw new Error("Agent module에 named mount/unmount export가 없습니다.");
    }
    if (generation !== mountGeneration) return;
    const tokenResponse = await api<{
      token: string;
      releaseId: string;
      session: SessionInfo;
    }>("/api/v1/session/agent-token", {
      method: "POST",
      body: JSON.stringify({ agentId: item.agentId }),
    });
    // Last chance to bail before anything reaches the DOM: past this point
    // the agent is mounted and has to be unmounted rather than dropped.
    if (generation !== mountGeneration) return;
    const bus = createEventBus();
    const initialPath = location.pathname
      .replace(`/agent/${encodeURIComponent(item.agentId)}`, "")
      .replace(/^\//, "");
    const context = createAgentContext({
      agentId: item.agentId,
      releaseId: tokenResponse.releaseId,
      basePath: `/agent/${item.agentId}`,
      initialPath,
      session: tokenResponse.session,
      theme: theme.value,
      locale: "ko-KR",
      config: {
        contractVersion: "1.0.0",
        classification: item.classification,
      },
      token: tokenResponse.token,
      baseUrl: "/api/v1",
      bus: bus.bus,
    });
    bus.onAgent("agent:title", ({ title }) => {
      document.title = `${title} · PwC AI Workspace`;
    });
    bus.onAgent("agent:navigate", ({ to }) => {
      navigate(`${context.basePath}/${to.replace(/^\//, "")}`);
    });
    bus.onAgent("agent:toast", ({ level, message }) => {
      window.dispatchEvent(
        new CustomEvent("mothership-toast", {
          detail: { level, message },
        }),
      );
    });
    const mounted: MountedAgent = {
      item,
      module: imported as AgentModule,
      container,
      bus,
      dirty: false,
    };
    bus.onAgent("agent:unsaved-changes", ({ dirty }) => {
      mounted.dirty = dirty;
    });
    bus.onAgent("agent:error", ({ message, fatal }) => {
      error.value = message;
      if (fatal) void unmountCurrent();
    });
    await imported.mount(container, context);
    // Tracked even if a switch landed during mount(), so the run queued
    // behind this one unmounts it properly instead of orphaning it.
    mountedAgent.value = mounted;
  } catch (reason) {
    error.value =
      reason instanceof Error ? reason.message : "Agent를 불러오지 못했습니다.";
  } finally {
    loading.value = false;
  }
}

async function loadAdmin() {
  if (!isAdmin.value) return;
  adminLoading.value = true;
  error.value = "";
  try {
    adminOverview.value = await adminApi("/api/admin/v1/overview");
    const payload = await adminApi<{ items: Record<string, unknown>[] }>(
      `/api/admin/v1/sections/${adminSection.value}`,
    );
    adminRows.value = payload.items;
  } catch (reason) {
    error.value =
      reason instanceof Error ? reason.message : "Admin 데이터를 읽지 못했습니다.";
  } finally {
    adminLoading.value = false;
  }
}

function resetAdminAction() {
  const action = adminAction.value;
  adminActionBody.value = JSON.stringify(action?.sample ?? {}, null, 2);
  adminActionResult.value = "";
}

async function runAdminAction() {
  const action = adminAction.value;
  if (!action) return;
  adminActionRunning.value = true;
  adminActionResult.value = "";
  error.value = "";
  try {
    if (action.form === "agent-origin") normalizeAgentOrigin(true);
    const body =
      action.form === "agent-origin"
        ? {
            scheme: agentOriginScheme.value,
            host: agentOriginHost.value,
          }
        : (JSON.parse(adminActionBody.value) as Record<string, unknown>);
    const result = await adminApi<Record<string, unknown>>(action.path, {
      method: action.method,
      headers: {
        "Idempotency-Key": crypto.randomUUID(),
      },
      body: JSON.stringify(body),
    });
    adminActionResult.value = JSON.stringify(result, null, 2);
    await loadAdmin();
  } catch (reason) {
    error.value =
      reason instanceof Error ? reason.message : "Admin command가 실패했습니다.";
  } finally {
    adminActionRunning.value = false;
  }
}

async function deleteAgent(agentId: string) {
  if (!agentId || deletingAgentId.value) return;
  const confirmed = window.confirm(
    `${agentId} 등록을 해제합니다.\n\n` +
      "release와 claim binding, capability grant, trigger가 삭제되고, " +
      "실행 이력이 참조하는 operation/tool/runtime profile은 비활성화됩니다.\n" +
      "record와 object, state, file, usage/audit 원장은 보존되며 " +
      "다시 등록하면 그대로 이어집니다.\n\n계속하시겠습니까?",
  );
  if (!confirmed) return;
  deletingAgentId.value = agentId;
  adminActionResult.value = "";
  error.value = "";
  try {
    const result = await adminApi<Record<string, unknown>>(
      `/api/admin/v1/agents/${encodeURIComponent(agentId)}`,
      {
        method: "DELETE",
        headers: { "Idempotency-Key": crypto.randomUUID() },
      },
    );
    adminActionResult.value = JSON.stringify(result, null, 2);
    if (selectedAgentId.value === agentId) selectedAgentId.value = "";
    // The registry list backs the agent switcher, so it has to drop the
    // agent too, not just the admin table.
    await Promise.all([
      loadAdmin(),
      api<RegistryItem[]>("/api/v1/registry/agents").then((rows) => {
        agents.value = rows;
      }),
    ]);
  } catch (reason) {
    error.value =
      reason instanceof Error
        ? reason.message
        : "Agent 등록 해제가 실패했습니다.";
  } finally {
    deletingAgentId.value = "";
  }
}

function selectAgent(agentId: string) {
  navigate(`/agent/${encodeURIComponent(agentId)}`);
}

function selectAdmin(section = "agents") {
  adminSection.value = section;
  navigate(`/admin/${section}`);
}

function toggleTheme() {
  mode.value = mode.value === "dark" ? "light" : "dark";
  localStorage.setItem("mothership-theme", mode.value);
  document.documentElement.dataset.theme = mode.value;
  mountedAgent.value?.bus.emitToAgent("host:theme-changed", theme.value);
}

function beforeUnload(event: BeforeUnloadEvent) {
  if (mountedAgent.value?.dirty) {
    event.preventDefault();
  }
}

watch(
  () => [route.value, selectedAgentId.value],
  async () => {
    if (isAdmin.value) {
      await unmountCurrent();
      const section = route.value.split("/")[2];
      if (section && adminSections.includes(section)) {
        adminSection.value = section;
      }
      await loadAdmin();
    } else {
      adminToken.value = "";
      adminTokenExpiresAt.value = 0;
      await mountSelected();
    }
  },
);

watch(adminSection, () => {
  adminActionIndex.value = 0;
  resetAdminAction();
  void loadAdmin();
});

onMounted(async () => {
  document.documentElement.dataset.theme = mode.value;
  window.addEventListener("popstate", parseRoute);
  window.addEventListener("beforeunload", beforeUnload);
  parseRoute();
  try {
    [hostSession.value, agents.value] = await Promise.all([
      api<SessionInfo>("/api/v1/session"),
      api<RegistryItem[]>("/api/v1/registry/agents"),
    ]);
    if (!isAdmin.value) {
      if (!selectedAgentId.value && agents.value[0]) {
        history.replaceState({}, "", `/agent/${agents.value[0].agentId}`);
        parseRoute();
      }
      await mountSelected();
    } else {
      resetAdminAction();
      await loadAdmin();
    }
  } catch (reason) {
    error.value =
      reason instanceof Error ? reason.message : "Host 초기화에 실패했습니다.";
  } finally {
    loading.value = false;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("popstate", parseRoute);
  window.removeEventListener("beforeunload", beforeUnload);
  void unmountCurrent("shutdown");
});
</script>

<template>
  <div class="shell">
    <aside class="sidebar">
      <button class="brand" type="button" @click="selectAgent(agents[0]?.agentId ?? '')">
        <span class="brand-mark">
          <img src="/pwc-logo-reversed.png" alt="PwC" />
        </span>
        <span>
          <strong>PwC AI Workspace</strong>
          <small>Agent Platform</small>
        </span>
      </button>

      <div class="nav-label">AGENTS</div>
      <nav class="agent-nav" aria-label="Agent navigation">
        <button
          v-for="agent in agents"
          :key="agent.agentId"
          type="button"
          :class="{ active: !isAdmin && selectedAgentId === agent.agentId }"
          @click="selectAgent(agent.agentId)"
        >
          <span class="agent-icon">{{ agent.name.slice(0, 1) }}</span>
          <span>
            <strong>{{ agent.name }}</strong>
            <small>{{ agent.frontendProfile }} · {{ agent.classification }}</small>
          </span>
        </button>
      </nav>

      <div class="nav-label">CONTROL PLANE</div>
      <button
        class="admin-link"
        :class="{ active: isAdmin }"
        type="button"
        @click="selectAdmin()"
      >
        <span class="agent-icon admin-icon">⌘</span>
        <span>
          <strong>Admin Console</strong>
          <small>Host-owned</small>
        </span>
      </button>

      <div class="sidebar-footer">
        <button type="button" class="theme-button" @click="toggleTheme">
          {{ mode === "dark" ? "☀" : "☾" }}
          {{ mode === "dark" ? "Light" : "Dark" }}
        </button>
        <div class="user-card" v-if="hostSession">
          <span class="avatar">{{ hostSession.user.name.slice(0, 1) }}</span>
          <span>
            <strong>{{ hostSession.user.name }}</strong>
            <small>{{ hostSession.user.role }}</small>
          </span>
        </div>
      </div>
    </aside>

    <main class="main">
      <header class="topbar">
        <div>
          <p class="eyebrow">{{ isAdmin ? "CONTROL PLANE" : "ACTIVE PRODUCT" }}</p>
          <h1>{{ isAdmin ? "Admin Console" : currentAgent?.name ?? "PwC AI Workspace" }}</h1>
        </div>
        <div class="status-pill">
          <span></span>
          Contract 1.0.0
        </div>
      </header>

      <div v-if="error" class="error-banner" role="alert">
        <strong>요청을 완료하지 못했습니다.</strong>
        <span>{{ error }}</span>
        <button type="button" @click="error = ''">×</button>
      </div>

      <section v-if="isAdmin" class="admin-layout">
        <nav class="admin-tabs" aria-label="Admin sections">
          <button
            v-for="section in adminSections"
            :key="section"
            type="button"
            :class="{ active: adminSection === section }"
            @click="selectAdmin(section)"
          >
            {{ section }}
          </button>
        </nav>
        <div class="overview-grid" v-if="adminOverview">
          <article v-for="(value, key) in adminOverview" :key="key">
            <span>{{ key }}</span>
            <strong v-if="typeof value !== 'object'">{{ value }}</strong>
            <strong v-else>view</strong>
          </article>
        </div>
        <div v-if="adminAction" class="command-panel">
          <nav
            v-if="sectionActions.length > 1"
            class="admin-tabs"
            aria-label="Admin commands"
          >
            <button
              v-for="(action, index) in sectionActions"
              :key="action.path"
              type="button"
              :class="{ active: adminActionIndex === index }"
              @click="selectAdminAction(index)"
            >
              {{ action.label }}
            </button>
          </nav>
          <div class="panel-heading">
            <div>
              <p class="eyebrow">NAMED ADMIN COMMAND</p>
              <h2>{{ adminAction.label }}</h2>
            </div>
            <code>{{ adminAction.method }} {{ adminAction.path }}</code>
          </div>
          <template v-if="adminAction.form === 'agent-origin'">
            <label for="agent-origin-host">Agent origin</label>
            <div class="origin-form">
              <select
                v-model="agentOriginScheme"
                aria-label="Scheme"
              >
                <option value="https">https</option>
                <option value="http">http</option>
              </select>
              <span class="origin-separator">://</span>
              <input
                id="agent-origin-host"
                v-model="agentOriginHost"
                placeholder="agents.internal.example.com (URL을 붙여넣으면 자동 파싱)"
                spellcheck="false"
                @input="normalizeAgentOrigin()"
                @change="normalizeAgentOrigin(true)"
              />
            </div>
            <p class="origin-hint">
              PwC AI Workspace만 접근 가능한 internal origin도 등록할 수 있습니다.
              origin의 <code>/agents.json</code>을 검증해 등록하고, 이후 모든
              asset 요청은 PwC AI Workspace가 proxy합니다.
            </p>
          </template>
          <template v-else>
            <label for="admin-command-body">JSON command body</label>
            <textarea
              id="admin-command-body"
              v-model="adminActionBody"
              rows="10"
              spellcheck="false"
            ></textarea>
          </template>
          <div class="command-actions">
            <button
              type="button"
              :disabled="adminActionRunning"
              @click="runAdminAction"
            >
              {{ adminActionRunning ? "Executing…" : "Execute command" }}
            </button>
            <button type="button" class="secondary" @click="resetAdminAction">
              Reset sample
            </button>
          </div>
          <pre v-if="adminActionResult">{{ adminActionResult }}</pre>
        </div>
        <div class="data-panel">
          <div class="panel-heading">
            <div>
              <p class="eyebrow">REGISTRY VIEW</p>
              <h2>{{ adminSection }}</h2>
            </div>
            <span>{{ adminRows.length }} records</span>
          </div>
          <div v-if="adminLoading" class="skeleton admin-skeleton"></div>
          <div v-else class="admin-cards">
            <article v-for="(row, index) in adminRows" :key="String(row.id ?? index)">
              <div
                v-for="(value, key) in row"
                :key="key"
                class="field-row"
              >
                <span>{{ key }}</span>
                <code v-if="typeof value === 'object'">{{ JSON.stringify(value) }}</code>
                <strong v-else>{{ value }}</strong>
              </div>
              <div
                v-if="adminSection === 'agents' && row.agentId"
                class="row-actions"
              >
                <button
                  type="button"
                  class="danger"
                  :disabled="deletingAgentId === String(row.agentId)"
                  @click="deleteAgent(String(row.agentId))"
                >
                  {{
                    deletingAgentId === String(row.agentId)
                      ? "등록 해제 중…"
                      : "등록 해제"
                  }}
                </button>
              </div>
            </article>
            <div v-if="!adminRows.length" class="empty-state">
              아직 등록된 항목이 없습니다.
            </div>
          </div>
        </div>
      </section>

      <section v-else class="agent-stage">
        <div v-show="loading" class="skeleton">
          <span></span><span></span><span></span>
        </div>
        <div ref="agentContainer" class="agent-container"></div>
      </section>
    </main>
  </div>
</template>
